import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { KatalogUprawnien, Uzytkownik } from '../api/typy';

interface OdpowiedzJa {
  uzytkownik: Uzytkownik | null;
  katalogUprawnien?: KatalogUprawnien;
}

interface Kontekst {
  uzytkownik: Uzytkownik | null;
  katalogUprawnien: KatalogUprawnien;
  wczytywanie: boolean;
  zaloguj: (login: string, haslo: string) => Promise<void>;
  wyloguj: () => Promise<void>;
  maUprawnienie: (...kody: string[]) => boolean;
}

const AuthContext = createContext<Kontekst | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['ja'],
    queryFn: () => api.get<OdpowiedzJa>('/auth/ja'),
    // Sesja może wygasnąć w tle albo administrator może zmienić rolę —
    // odświeżamy przy powrocie do okna.
    refetchOnWindowFocus: true,
    retry: false,
  });

  const logowanie = useMutation({
    mutationFn: (dane: { login: string; haslo: string }) =>
      api.post<{ uzytkownik: Uzytkownik }>('/auth/logowanie', dane),
    onSuccess: () => qc.invalidateQueries(),
  });

  const wylogowanie = useMutation({
    mutationFn: () => api.post('/auth/wylogowanie'),
    onSuccess: () => {
      qc.setQueryData(['ja'], { uzytkownik: null });
      qc.clear();
    },
  });

  const zaloguj = useCallback(
    async (login: string, haslo: string) => {
      await logowanie.mutateAsync({ login, haslo });
    },
    [logowanie],
  );

  const wyloguj = useCallback(async () => {
    await wylogowanie.mutateAsync();
  }, [wylogowanie]);

  const uzytkownik = data?.uzytkownik ?? null;

  const maUprawnienie = useCallback(
    (...kody: string[]) => kody.some((k) => uzytkownik?.uprawnienia.includes(k)),
    [uzytkownik],
  );

  const wartosc = useMemo<Kontekst>(
    () => ({
      uzytkownik,
      katalogUprawnien: data?.katalogUprawnien ?? {},
      wczytywanie: isLoading,
      zaloguj,
      wyloguj,
      maUprawnienie,
    }),
    [uzytkownik, data?.katalogUprawnien, isLoading, zaloguj, wyloguj, maUprawnienie],
  );

  return <AuthContext.Provider value={wartosc}>{children}</AuthContext.Provider>;
}

export function useAuth(): Kontekst {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth poza AuthProvider');
  return ctx;
}
